/**
 * Account Policy — restrict LLM vendors to company accounts only.
 *
 * Enforced on the endpoint proxy (see bdr-agent proxy/account_gate.rs): vendor
 * tenant headers are injected (Gemini, ChatGPT, Claude) and requests whose
 * account can be identified but is not a company one are audited or blocked.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from './ui';
import { usePermission } from '../hooks/usePermission';
import { useTranslation } from 'react-i18next';

interface Policy {
  mode: 'off' | 'audit' | 'block';
  allowed_domains: string[];
  chatgpt_workspace_ids: string[];
  anthropic_org_ids: string[];
  anthropic_header: string;
  api_key_fingerprints: string[];
  deny_hosts: string[];
}

const EMPTY: Policy = {
  mode: 'off',
  allowed_domains: [],
  chatgpt_workspace_ids: [],
  anthropic_org_ids: [],
  anthropic_header: '',
  api_key_fingerprints: [],
  deny_hosts: [],
};

const lines = (v: string[]) => v.join('\n');
const parse = (v: string) => v.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);

const getFIELDS = (t: (key: string) => string): Array<{ key: keyof Policy; label: string; help: string; placeholder: string }> => [
  { key: 'allowed_domains', label: t('components.accountPolicyPanel.companyEmailDomains'), help: t('components.accountPolicyPanel.geminiGoogleDomainRestrictionPlusThe'), placeholder: 'acme.com' },
  { key: 'chatgpt_workspace_ids', label: t('components.accountPolicyPanel.chatgptWorkspaceIDs'), help: t('components.accountPolicyPanel.sentAsChatGPTAllowedWorkspaceId'), placeholder: t('components.accountPolicyPanel.workspaceId') },
  { key: 'anthropic_org_ids', label: t('components.accountPolicyPanel.claudeOrganizationIDs'), help: t('components.accountPolicyPanel.checkedAgainstTheOrganizationInClaude'), placeholder: '00000000-0000-0000-0000-000000000000' },
  { key: 'api_key_fingerprints', label: t('components.accountPolicyPanel.companyAPIKeyFingerprints'), help: t('components.accountPolicyPanel.first12HexCharsOfThe'), placeholder: t('components.accountPolicyPanel.sha256Prefix') },
  { key: 'deny_hosts', label: t('components.accountPolicyPanel.deniedHosts'), help: t('components.accountPolicyPanel.vendorsWithNoCompanyTenantControl'), placeholder: 'chat.deepseek.com' },
];

const AccountPolicyPanel: React.FC<{ apiUrl: string }> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const canWrite = usePermission('update:policy');
  const [p, setP] = useState<Policy>(EMPTY);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/admin/bdr/account-policy`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data: Policy = { ...EMPTY, ...(await r.json()) };
      setP(data);
      setDrafts(Object.fromEntries(getFIELDS(t).map((f) => [f.key, lines(data[f.key] as string[])])));
    } catch (e: any) {
      setMsg({ kind: 'error', text: `Could not load account policy: ${e.message}` });
    }
  }, [apiUrl]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const body: Policy = { ...p };
      for (const f of getFIELDS(t)) (body as any)[f.key] = parse(drafts[f.key] || '');
      const r = await fetch(`${apiUrl}/admin/bdr/account-policy`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setP({ ...EMPTY, ...(await r.json()) });
      setMsg({ kind: 'ok', text: t('components.accountPolicyPanel.savedPolicyPickup') });
    } catch (e: any) {
      setMsg({ kind: 'error', text: `Save failed: ${e.message}` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {msg && (
        <div className={`notice ${msg.kind === 'error' ? 'error' : ''}`}>
          <Icon name={msg.kind === 'error' ? 'warning' : 'checkCircle'} /> {msg.text}
        </div>
      )}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">{t('components.accountPolicyPanel.companyAccountOnlyEnforcement')}</div>
        <div className="ent-card-body">
          <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>{t('components.accountPolicyPanel.mode')}</label>
          <select
            className="ent-input"
            style={{ maxWidth: 260 }}
            value={p.mode}
            disabled={!canWrite || busy}
            onChange={(e) => setP({ ...p, mode: e.target.value as Policy['mode'] })}
          >
            <option value="off">{t('components.accountPolicyPanel.off')}</option>
            <option value="audit">{t('components.accountPolicyPanel.auditLogNonCompanyAccounts')}</option>
            <option value="block">{t('components.accountPolicyPanel.blockRefuseNonCompanyAccounts')}</option>
          </select>

          {getFIELDS(t).map((f) => (
            <div key={f.key} style={{ marginTop: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>{f.label}</label>
              <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 4 }}>{f.help}</div>
              <textarea
                className="ent-input"
                rows={3}
                style={{ width: '100%', maxWidth: 520, fontFamily: 'monospace', fontSize: 12 }}
                placeholder={f.placeholder}
                value={drafts[f.key] || ''}
                disabled={!canWrite || busy}
                onChange={(e) => setDrafts((d) => ({ ...d, [f.key]: e.target.value }))}
              />
            </div>
          ))}

          <div style={{ marginTop: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>{t('components.accountPolicyPanel.claudeOrgHeaderNameOptional')}</label>
            <input
              className="ent-input"
              style={{ maxWidth: 320 }}
              placeholder={t('components.accountPolicyPanel.anthropicAllowedOrgIds')}
              value={p.anthropic_header}
              disabled={!canWrite || busy}
              onChange={(e) => setP({ ...p, anthropic_header: e.target.value })}
            />
          </div>

          {canWrite ? (
            <button className="ent-btn" style={{ marginTop: 16 }} disabled={busy} onClick={save}>
              {busy ? t('components.accountPolicyPanel.saving') : t('components.accountPolicyPanel.savePolicy')}
            </button>
          ) : (
            <div className="notice" style={{ marginTop: 16 }}>
              <Icon name="lock" /> {t('components.accountPolicyPanel.youCanViewThisPolicyBut')}
            </div>
          )}
        </div>
      </div>
      <p className="subtitle" style={{ fontSize: 11 }}>
        <Icon name="info" /> {t('components.accountPolicyPanel.blocksAndAuditEventsAppearIn')} <code>account-policy</code>{t('components.accountPolicyPanel.deepseekCursorAndGrokHaveNo')}
      </p>
    </>
  );
};

export default AccountPolicyPanel;
